import { trans as translate } from '../../util/transHelpers';
import { getDateTime } from '@shared/utils/timeUtils';

const ERROR = {
	EVENT_ENDED_BUT_NO_DVR_OR_VOD_AVAILABLE: 'error_event_ended_but_no_dvr_or_vod_available',
	EVENT_ENDED_BUT_NO_STREAM_WAS_AVAILABLE: 'error_event_ended_but_no_stream_was_available',
	EVENT_STARTED_BUT_NO_STREAM_AVAILABLE:   'error_event_started_but_no_stream_available',
	NO_STREAM_AVAILABLE:                     'error_no_stream_available',
	STREAM_FATAL_ERROR:                      'error_stream_fatal_error',
	EVENT_WAS_MADE_PRIVATE:                  'error_event_was_made_private',
	EVENT_VOD_PROCESSING:                    'error_event_vod_processing',
	EVENT_VOD_FAILURE:                       'error_event_vod_failure',
	EVENT_HAS_PLACEHOLDER:                   'error_event_placeholder',
	EVENT_PAYMENT_REQUIRED:                  'error_event_payment_required',
	EVENT_PLAYOUT_FAILED:                    'error_event_playout_failed',
	EVENT_GEOBLOCKED:                        'error_event_geoblocked',
	PARALLEL_STREAM_DETECTED:                'error_parallel_stream_detected',
	EVENT_AUTH_REQUIRED:                     'error_event_auth_required',
	EVENT_NOT_PUBLISHED_YET:                 'error_event_not_published_yet',
	EVENT_PUBLISH_ENDED:                     'error_event_publish_ended',
};

const PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY = 'exception.player.error_please_wait';

const errorMap = {
	/// /////////////////////////
	/// Tagging Tool Errors
	/// /////////////////////////
	[ERROR.NO_STREAM_AVAILABLE]: {
		context:  'tagging-tool',
		priority: 0,
		text:     {
			title:    'exception.player.error_no_stream_available',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStream = (event.streams ?? []).some((stream) => Boolean(stream.startedAt));
			const videoAvailable = event.videoAvailable;
			return isLiveEvent && !hasStream && !videoAvailable;
		},
	},
	/// /////////////////////////
	/// Arena Errors
	/// /////////////////////////

	// P0 — Technical failure / broken experience (should win over “wait” and most content-state)
	[ERROR.STREAM_FATAL_ERROR]: {
		priority: 0,
		text:     {
			title: 'exception.player.error_stream_fatal_error',
		},
		match: ({ playerError }) => {
			return playerError && playerError.fatal === true;
		},
	},
	[ERROR.EVENT_PLAYOUT_FAILED]: {
		priority: 0,
		text:     {
			title: 'exception.player.error_event_playout_failed',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_PLAYOUT_FAILED;
		},
	},
	// P1 — Access blocks (user action required, not “try later”)
	[ERROR.EVENT_AUTH_REQUIRED]: {
		priority: 10,
		text:     {
			title: 'exception.player.error_event_auth_required',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_AUTH_REQUIRED;
		},
	},
	[ERROR.EVENT_PAYMENT_REQUIRED]: {
		priority: 11,
		text:     {
			title: 'exception.player.error_event_payment_required',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_PAYMENT_REQUIRED;
		},
	},
	[ERROR.EVENT_GEOBLOCKED]: {
		priority: 12,
		text:     {
			title: 'exception.player.error_event_geoblocked',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_GEOBLOCKED;
		},
	},
	[ERROR.PARALLEL_STREAM_DETECTED]: {
		priority: 13,
		text:     {
			title: 'exception.player.error_parallel_stream_detected',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.PARALLEL_STREAM_DETECTED;
		},
	},
	// P2 — Publication window (content intentionally unavailable)
	[ERROR.EVENT_NOT_PUBLISHED_YET]: {
		priority:        20,
		ignoreInContext: ['tagging-tool', 'manager'],
		text:            {
			title: ({ event }) => {
				const publishStart = event.publishStart || event.publish_start;
				const publishStartTime = new Date(publishStart).getTime() / 1000;
				return translate('exception.player.error_event_not_published_yet', { date: getDateTime(publishStartTime) });
			},
		},
		match: ({ event, currentTime, playoutError }) => {
			if (playoutError === ERROR.EVENT_NOT_PUBLISHED_YET) {
				return true;
			}
			const publishStart = event.publishStart || event.publish_start;
			if (!publishStart) {
				return false;
			}
			const publishStartTime = new Date(publishStart).getTime() / 1000;
			const time = currentTime || Math.floor(Date.now() / 1000);
			return time < publishStartTime;
		},
	},
	[ERROR.EVENT_PUBLISH_ENDED]: {
		priority:        21,
		ignoreInContext: ['tagging-tool', 'manager'],
		text:            {
			title: 'exception.player.error_event_publish_ended',
		},
		match: ({ event, currentTime, playoutError }) => {
			if (playoutError === ERROR.EVENT_PUBLISH_ENDED) {
				return true;
			}
			const publishEnd = event.publishEnd || event.publish_end;
			if (!publishEnd) {
				return false;
			}
			const publishEndTime = new Date(publishEnd).getTime() / 1000;
			const time = currentTime || Math.floor(Date.now() / 1000);
			return time > publishEndTime;
		},
	},
	// P3 — Intentional editorial messaging (should win over “wait” and most content-state)
	[ERROR.EVENT_WAS_MADE_PRIVATE]: {
		priority: 30,
		text:     {
			title: 'exception.player.error_event_was_made_private',
		},
		match: ({ event }) => Boolean(event.wasMadePrivate),
	},
	[ERROR.EVENT_HAS_PLACEHOLDER]: {
		ignoreInContext: ['tagging-tool'],
		priority:        31,
		text:            {
			title: ({ event }) => {
				const placeholder = JSON.parse(event.placeholderText);
				return placeholder?.[$arenaConfig.currentLanguageKey] || placeholder?.[Object.keys(placeholder)[0]];
			},
		},
		match: ({ event }) => {
			// check if location has disablePlaceholderScreen. If so, return false
			if (window.location.search.includes('disablePlaceholderScreen')) {
				return false;
			}
			return Boolean(event.placeholder);
		},
	},
	// P4 — “Wait / content-state” cases (should lose to everything above)
	[ERROR.EVENT_STARTED_BUT_NO_STREAM_AVAILABLE]: {
		priority: 40,
		text:     {
			title:    'exception.player.error_event_started_but_no_stream_available',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStream = (event.streams ?? []).some((stream) => Boolean(stream.startedAt));
			const isLiveRightNow = event.startTimestamp <= currentTime && currentTime <= event.endTimestamp;
			return isLiveEvent && !hasStream && isLiveRightNow;
		},
	},
	[ERROR.EVENT_VOD_PROCESSING]: {
		priority: 41,
		text:     {
			title:    'exception.player.error_event_vod_processing',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event }) => {
			return !event.videoAvailable && event.videoProcessing;
		},
	},
	[ERROR.EVENT_ENDED_BUT_NO_STREAM_WAS_AVAILABLE]: {
		priority: 42,
		text:     {
			title: 'exception.player.error_event_ended_but_no_stream_was_available',
		},
		match: ({ event, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStreamStarted = (event.streams ?? []).some((stream) => Boolean(stream.startedAt));
			const videoAvailable = event.videoAvailable;
			const hasEnded = currentTime > event.endTimestamp;
			return isLiveEvent && !hasStreamStarted && hasEnded && !videoAvailable;
		},
	},
	[ERROR.EVENT_ENDED_BUT_NO_DVR_OR_VOD_AVAILABLE]: {
		priority: 43,
		text:     {
			title:    'exception.player.error_event_ended_but_no_dvr_or_vod_available',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},

		match: ({ event, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const videoAvailable = event.videoAvailable;
			const hasEnded = currentTime > event.endTimestamp;
			return isLiveEvent && !videoAvailable && hasEnded;
		},
	},
	[ERROR.EVENT_VOD_FAILURE]: {
		priority: 44,
		text:     {
			title: 'exception.player.error_event_vod_failure',
		},

		match: ({ event, currentTime }) => {
			const hasEnded = currentTime > event.endTimestamp;
			const videoAvailable = event.videoAvailable;
			return !videoAvailable && hasEnded;
		},
	},
};

class ErrorScreen {
	errorKey = null;
	event = null;
	currentTime = null;
	config = null;
	context = null;

	constructor({ errorKey, event, currentTime, config, context }) {
		if (errorKey !== null && !errorMap[errorKey]) {
			throw new Error(`Unknown error key: ${errorKey}`);
		}
		this.errorKey = errorKey;
		this.event = event;
		this.currentTime = currentTime;
		this.config = config;
		this.context = context;
	}

	static fromContext({ event, playerError, playoutError, currentTime, context }) {
		const error = Object.entries(errorMap).reduce(
			(primaryError, [key, config]) => {
				const ignore = config.ignoreInContext && config.ignoreInContext.includes(context);
				if (
					(primaryError.key === null || primaryError.config.priority < config.priority) &&
					((context && config.context === context) || !config.context) && !ignore &&
					config.match({
						event,
						playerError,
						playoutError,
						currentTime,
					})
				) {
					return {
						key,
						config,
					};
				}
				return primaryError;
			},
			{ key: null, config: {} }
		);
		if (error.key) {
			return new ErrorScreen({ errorKey: error.key, event, currentTime, config: error.config, context });
		}
		return null;
	}

	get error() {
		if (!this.errorKey) {
			return null;
		}
		return errorMap[this.errorKey];
	}

	get title() {
		if (!this.error) {
			return null;
		}
		if (typeof this.error.text.title === 'function') {
			return this.error.text.title({ event: this.event });
		}
		return translate(this.error.text.title);
	}

	get subtitle() {
		if (!this.error || !this.error.text.subtitle) {
			return null;
		}
		return translate(this.error.text.subtitle);
	}
}

export default ErrorScreen;
export { ErrorScreen, ERROR };
